/**
 * Provider selection — the single swap point between fixture and live data.
 *
 * `VITE_DATA_SOURCE=fixture`            → typed fixtures from `src/fixtures/`. Also the default
 *                                          in `vite dev` and in the test suite.
 * `VITE_DATA_SOURCE=http`               → the real façade via `createHttpProvider`.
 *
 * A production build (`import.meta.env.PROD`, which `vite build` inlines as `true`) must say which
 * one. With the variable absent it throws at boot instead of serving fixtures to an operator: the
 * bundle otherwise carries `?? 'fixture'` into the deploy, and `createProvider` below binds
 * `seedMirror(fixtureEffectiveFor('owner'))` — eleven pages of a fake fleet, rendered as an
 * authorised operator. DA-56.
 *
 * HTTP mode is a *human session*, not a machine client. That is plan D1's ruling: the console
 * signs in through `POST /ops/v1/sessions` and carries the short-lived session JWT the server
 * minted with its own key, so nothing here mints, signs, or holds a client assertion — the
 * assertion would need the service's private key in the browser, which `AGENTS.md` invariant ۴
 * forbids and `docs/RBAC_AND_CONSOLE_FA.md` §۱۲ refuses for a console that only runs in a UI.
 *
 * What lives in this file is therefore thin: build the provider, hand it the session token from
 * `api/session.ts`, and tell that session when a door answers 401 so a revoked login stops
 * rendering as a healthy page. When there is no session, `http` mode is not an error —
 * `<RequireSession>` in `app/routes.tsx` shows the login screen.
 *
 * A genuinely misconfigured request still fails loudly rather than quietly calling an API it
 * cannot authorise: no `VITE_DATA_SOURCE` at all in a production build, an unknown
 * `VITE_DATA_SOURCE`, and a `token` that is not a provider.
 */

import { createHttpProvider, type TokenProvider } from './client';
import type { ConsoleApi } from './consoleApi';
import { createFixtureApi, type FixtureProviderOptions } from './fixtureApi';
import { bindSessionApi, noteUnauthorized, seedMirror, tokenProvider } from './session';
import { fixtureEffectiveFor } from '../fixtures/session';

export type DataSource = 'fixture' | 'http';

export interface ProviderConfig {
  dataSource: DataSource;
  baseUrl: string;
  routerBase: string;
}

export const DEFAULT_INTEGRATION_PREFIX = '/integration/v1';

/**
 * What a production build must say, and why the default cannot speak for it (DA-56).
 *
 * The message is an instruction: it names the variable, both legal values, and the reason the
 * fallback exists — and it deliberately does not use the word `got`, so a reader (and the test
 * above) can tell "absent" apart from "unknown value" without parsing prose.
 */
const PRODUCTION_NEEDS_A_SOURCE =
  'VITE_DATA_SOURCE is not set. A production build must name its data source: ' +
  '"http" for the live façade, or "fixture" only for a deliberate demo build. ' +
  'The fixture default exists for `vite dev` and the test suite, not for a deployed console.';

export function readProviderConfig(env: ImportMetaEnv = import.meta.env): ProviderConfig {
  // `env.PROD === true`, not `env.PROD`: a hand-built env in a test has no such key, and there
  // `undefined` must read as "not a production build" rather than as one.
  if (env.VITE_DATA_SOURCE === undefined && env.PROD === true) {
    throw new Error(PRODUCTION_NEEDS_A_SOURCE);
  }
  const raw = (env.VITE_DATA_SOURCE ?? 'fixture').trim().toLowerCase();
  if (raw !== 'fixture' && raw !== 'http') {
    throw new Error(`VITE_DATA_SOURCE must be "fixture" or "http", got "${raw}"`);
  }
  return {
    dataSource: raw,
    baseUrl: (env.VITE_API_BASE_URL ?? DEFAULT_INTEGRATION_PREFIX).trim(),
    routerBase: (env.VITE_UI_BASE ?? '/').trim() || '/',
  };
}

export interface CreateProviderArgs {
  config?: ProviderConfig;
  /**
   * Only for an embedding host that already holds a session token by some other means. Left out,
   * `http` mode reads the bearer from `api/session.ts` — the module that owns the human session.
   */
  token?: TokenProvider;
  /** Only for `http`: what to do when a door answers 401. Defaults to ending the session. */
  onUnauthorized?: (status: number) => void;
  fixtureOptions?: FixtureProviderOptions;
}

export function createProvider(args: CreateProviderArgs = {}): ConsoleApi {
  const config = args.config ?? readProviderConfig();
  if (config.dataSource === 'fixture') {
    const api = createFixtureApi(args.fixtureOptions ?? {});
    bindSessionApi(api);
    // The demo publishes the owner row of §۴ as its mirror, because a console that answered
    // «نمی‌دانم» on all eleven pages would no longer demo them. `http` mode seeds nothing: there the
    // only mirror is the one `signIn` reads off `permissions/effective`.
    seedMirror(fixtureEffectiveFor('owner'));
    return api;
  }
  if (args.token !== undefined && typeof args.token !== 'function') {
    throw new Error(
      'createProvider({ token }): `token` must be a TokenProvider function — ' +
        'it is called once per request and its return value becomes the bearer.',
    );
  }
  const api = createHttpProvider({
    baseUrl: config.baseUrl,
    token: args.token ?? tokenProvider(),
    onUnauthorized: args.onUnauthorized ?? noteUnauthorized,
  });
  bindSessionApi(api);
  return api;
}
